/**
 * @file ColorContrastNote.tsx
 * @description Строка контраста под полем цвета (phase19.16, партия SB120 —
 * предупреждение, не запрет; uiux-sb120-fields §3). Число — всегда, когда
 * фон известен:
 *  - ✓ от 4,5 — спокойная строка «Контраст с фоном 7,2:1»;
 *  - ! от 3 до 4,5 — «читается только крупный текст»;
 *  - ✕ ниже 3 — «текст почти не виден».
 * Оба слабых — жёлтая полоса (`warning`, не `error`: выбор не запрещён и
 * чтец экрана не перебивается на каждом касании) с кнопкой «Взять читаемый:
 * <роль>». Значок уровня у полосы — её иконка, символ «!»/«✕» в тексте не
 * повторяем (QA партии SB120). Под элементом фото — «проверьте на канве».
 */

import { SITE_CONTRAST_LEVEL_MARKS, type SiteContrastLevel } from "@chaiberi/shared";

import { Button } from "../ui/button";
import { NoticeStrip } from "../ui/NoticeStrip";
import { contrastView } from "../../lib/site-fields/color-contrast";

interface Props {
  mode: "text" | "fill";
  ratio: number | null;
  photo?: boolean;
  readable: { label: string; select: () => void } | null;
}

/** Текст слабого контраста — реестром по уровню (OCP). */
const WEAK_TEXT: Readonly<Record<Exclude<SiteContrastLevel, "ok">, string>> = {
  warn: "читается только крупный текст.",
  fail: "текст почти не виден.",
};

export function ColorContrastNote({ mode, ratio, photo = false, readable }: Props) {
  if (photo) return <p className="type-caption text-on-surface-variant">Под элементом фото — читаемость проверьте на канве.</p>;
  if (ratio === null) return null;
  const view = contrastView(ratio);
  const subject = mode === "fill" ? "Контраст текста на заливке" : "Контраст с фоном";
  if (view.level === "ok") {
    return (
      <p className="type-caption text-on-surface-variant">
        {SITE_CONTRAST_LEVEL_MARKS.ok} {subject} {view.label}
      </p>
    );
  }
  return (
    <NoticeStrip tone="warning">
      <span>
        {subject} {view.label} — {WEAK_TEXT[view.level]}
      </span>
      {readable ? (
        <Button variant="outline" className="mt-2 flex h-auto min-h-12 max-w-full whitespace-normal py-2 text-left" onClick={readable.select}>
          Взять читаемый: {readable.label}
        </Button>
      ) : null}
    </NoticeStrip>
  );
}
